<template>
  <!-- Direction: LTR by default in every locale (low→high number-line gauge).
       Authors opt into RTL mirroring per-instance via `flipOnRtl` — when set AND
       the language is RTL, the number line reads right→low to left→high. We
       mirror through the single `bmiToX` coordinate function so the SVG bar
       dots/handles and the HTML overlays (pill, dash, stop labels, positioned by
       left:<pct>% via bmiToPct) stay aligned, and flip the gradient vector
       (x1/x2). Otherwise we force dir="ltr". Physical `translateX(-50%)`
       centering is untouched. -->
  <div class="bmi-visual" :dir="dirAttr">
    <div v-if="fields.title?.key" class="bmi-title">
      <TipTap v-model="titleValue" mode="single" />
    </div>
    <div v-if="fields.targetText?.key" class="bmi-target-text">
      <TipTap v-model="targetTextValue" mode="single" />
    </div>

    <div class="bmi-chart-wrap">
      <div
        v-if="fields.handleLabel?.key && targetBmiSet"
        class="bmi-pill"
        :style="{ left: animatedPctCss }"
      >
        <TipTap v-model="handleLabelValue" mode="single" />
      </div>
      <div
        v-if="fields.handleLabel?.key && targetBmiSet"
        class="bmi-dash"
        :style="{ left: animatedPctCss }"
      ></div>

      <div class="bmi-stops-row">
        <div
          v-for="stop in parsedStops"
          :key="stop.id"
          class="bmi-stop"
          :style="{ left: `${bmiToPct(stop.value)}%` }"
        >
          <TipTap
            v-if="stop.labelKey && hasTranslationContent(stop.labelKey)"
            :model-value="getStopTranslation(stop.labelKey)"
            @update:model-value="setStopTranslation(stop.labelKey, $event)"
            mode="single"
          />
          <span v-else>{{ stop.raw }}</span>
        </div>
      </div>

      <svg
        class="bmi-chart-svg"
        :viewBox="`0 0 ${VB_W} ${VB_H}`"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="BMI range with current and target markers"
      >
        <defs>
          <linearGradient :id="gradId" :x1="flipRtl ? 1 : 0" y1="0" :x2="flipRtl ? 0 : 1" y2="0">
            <stop
              v-for="(stop, i) in resolvedGradientStops"
              :key="i"
              :offset="`${stop.position}%`"
              :stop-color="stop.color"
            />
          </linearGradient>
        </defs>

        <rect
          class="bmi-bar"
          :x="BAR_X0"
          :y="BAR_Y"
          :width="MAX_X - MIN_X + 2 * BAR_RADIUS"
          :height="BAR_H"
          :rx="BAR_H / 2"
          :fill="`url(#${gradId})`"
        />

        <circle
          v-for="stop in parsedStops"
          :key="'dot-' + stop.id"
          :cx="bmiToX(stop.value)"
          :cy="BAR_Y + BAR_H / 2"
          r="3"
          fill="white"
        />

        <rect
          v-if="currentBmiSet"
          class="handle handle-current"
          :x="currentX - 6"
          :y="HANDLE_Y"
          width="12"
          :height="HANDLE_HEIGHT"
          rx="6"
        />
        <rect
          v-if="targetBmiSet"
          class="handle handle-target"
          :x="animatedX - 6"
          :y="HANDLE_Y"
          width="12"
          :height="HANDLE_HEIGHT"
          rx="6"
        />
      </svg>
    </div>

    <div class="bmi-bottom-row">
      <div class="bmi-bottom-left">
        <TipTap v-if="fields.leftBottomLabel?.key" v-model="leftBottomValue" mode="single" />
      </div>
      <div class="bmi-bottom-right">
        <TipTap v-if="fields.rightBottomLabel?.key" v-model="rightBottomValue" mode="single" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, useId, watch } from 'vue'
import { PipeDisplayMode, isInPipeContext, usePipe } from '@render/composables/usePipe'
import { useComponentProcessors } from '@render/composables/useComponentProcessors'
import { useEditorCommands } from '@render/composables/useEditorCommands'
import { useComponentScopedVariables } from '@render/composables/useComponentScopedVariables'
import { useDirection } from '@render/composables/useDirection'
import {
  getColorStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '@render/composables/useStyleData'
import TipTap from '@render/lib/TipTap'
import type { ColorField, SizeField } from '@render/types/styles'
import type { VariableKeyField } from '@api/types.gen'
import type { BMIVisualFields as Fields, AnimationTrigger, AnimationEasing } from './definition'

const fields = defineModel<Fields>('fields', {
  required: true,
  default: (): Fields => ({}),
})

const pipe = isInPipeContext() ? usePipe() : null

const { isRtl, dir } = useDirection()

// Mirror only when the author opted in (`flipOnRtl`) AND the language is RTL.
const flipRtl = computed(() => isRtl.value && fields.value.flipOnRtl === true)
const dirAttr = computed<'rtl' | 'ltr'>(() => (fields.value.flipOnRtl ? dir.value : 'ltr'))

// ---------- translations ----------

function translationModel(keyPath: () => string | undefined) {
  return computed<object>({
    get: () => {
      const key = keyPath()
      if (!key || !pipe) return {}
      return pipe.getTranslation(key) ?? {}
    },
    set: (val) => {
      const key = keyPath()
      if (!key || !pipe) return
      pipe.setTranslation(key, val)
    },
  })
}

const titleValue = translationModel(() => fields.value.title?.key)
const targetTextValue = translationModel(() => fields.value.targetText?.key)
const handleLabelValue = translationModel(() => fields.value.handleLabel?.key)
const leftBottomValue = translationModel(() => fields.value.leftBottomLabel?.key)
const rightBottomValue = translationModel(() => fields.value.rightBottomLabel?.key)

// Per-stop translation accessors — stops are dynamic (added/removed via the
// editor) so we can't pre-create writable computeds; bind via direct getter
// + setter instead.
function getStopTranslation(key: string | undefined): object {
  if (!key || !pipe) return {}
  return pipe.getTranslation(key) ?? {}
}
function setStopTranslation(key: string | undefined, val: object | undefined): void {
  if (!key || !pipe || !val) return
  pipe.setTranslation(key, val)
}

// Translation keys are pre-populated for the default stops in definition.ts
// but the matching translations may not be registered yet (the runtime
// doesn't auto-process `defaultText`). Walk the TipTap doc to detect whether
// the translation actually has any text — when empty, the renderer falls
// back to displaying option.value as plain text so default stops are
// readable out of the box.
function hasTranslationContent(key: string | undefined): boolean {
  if (!key || !pipe) return false
  const doc = pipe.getTranslation(key)
  if (!doc) return false
  const visit = (n: unknown): boolean => {
    if (!n || typeof n !== 'object') return false
    const node = n as { type?: string; text?: string; content?: unknown[] }
    if (node.type === 'text' && typeof node.text === 'string' && node.text.length > 0) return true
    if (Array.isArray(node.content)) return node.content.some(visit)
    return false
  }
  return visit(doc)
}

// ---------- geometry ----------
// SVG only contains the bar, dots, handles, and dashed connector. Stop labels
// live in an HTML row above the SVG. Handle sits flush with viewBox top so
// the SVG aspect-ratio (342 / VB_H) is tight; the dash extends with negative
// y via overflow:visible to reach up under the pill.
const VB_W = 342
const VB_H = 40
const BAR_X0 = 6
const BAR_X1 = 336
const HANDLE_HEIGHT = 39
const BAR_H = 12
const HANDLE_Y = 0
const BAR_Y = HANDLE_Y + (HANDLE_HEIGHT - BAR_H) / 2

// Stops and handles align with the bar's rounded-corner *centers* (not the
// bar's outer edges) so the leftmost/rightmost stop sits on the midpoint of
// each rounded cap. Effective range is the bar width minus one radius on
// each side.
const BAR_RADIUS = BAR_H / 2
const MIN_X = BAR_X0 + BAR_RADIUS
const MAX_X = BAR_X1 - BAR_RADIUS
const EFFECTIVE_W = MAX_X - MIN_X

const gradId = `bmi-grad-${useId()}`

function clamp(v: number, a: number, b: number): number {
  return Math.max(a, Math.min(b, v))
}

// Fallbacks used when no stops are configured — keep the original 15–35
// design domain so the chart is still meaningful out of the box.
const FALLBACK_MIN = 15
const FALLBACK_MAX = 35

interface ParsedStop {
  id: string
  value: number
  raw: string
  labelKey: string | undefined
}

const parsedStops = computed<ParsedStop[]>(() => {
  const stops = fields.value.stops?.stops ?? []
  return stops
    .map(
      (s): ParsedStop => ({
        id: s.id,
        value: Number(s.value),
        raw: String(s.value),
        labelKey: s.label?.key || undefined,
      }),
    )
    .filter((s) => Number.isFinite(s.value))
    .sort((a, b) => a.value - b.value)
})

const minStop = computed(() => parsedStops.value[0]?.value ?? FALLBACK_MIN)
const maxStop = computed(
  () => parsedStops.value[parsedStops.value.length - 1]?.value ?? FALLBACK_MAX,
)

function bmiToX(bmi: number): number {
  const min = minStop.value
  const max = maxStop.value
  if (max <= min) return MIN_X
  const t = clamp((bmi - min) / (max - min), 0, 1)
  // When mirrored the axis reads right→low to left→high: minStop sits at MAX_X
  // (right) and grows leftward. Every derived position (bmiToPct, currentX,
  // animatedX, dots, handles, HTML overlays) mirrors through this one function.
  return flipRtl.value ? MAX_X - t * EFFECTIVE_W : MIN_X + t * EFFECTIVE_W
}

function bmiToPct(bmi: number): number {
  return (bmiToX(bmi) / VB_W) * 100
}

function readVarNumber(v: VariableKeyField | undefined, fallback: number): number {
  if (!v?.key || !pipe) return fallback
  const raw = pipe.getData(`var.${v.key}`)
  const n = Number(raw)
  return Number.isFinite(n) ? n : fallback
}

// Editor preview values used when the bound BMI variable isn't set. Picked
// as 70% / 30% of the active stop range so they stay sensible regardless of
// the stops the author has configured (e.g. 15–35 → 29 / 21; 18–30 → 26.4 /
// 21.6). Lets the author preview the pill, dashed connector, and animation
// without needing live pipe data.
function previewCurrentBmi(): number {
  return minStop.value + (maxStop.value - minStop.value) * 0.7
}
function previewTargetBmi(): number {
  return minStop.value + (maxStop.value - minStop.value) * 0.3
}

const currentBmi = computed(() => {
  if (isBmiVarSet(fields.value.currentBmiVariable)) {
    return clamp(
      readVarNumber(fields.value.currentBmiVariable, minStop.value),
      minStop.value,
      maxStop.value,
    )
  }
  if (isEditor.value) {
    return clamp(previewCurrentBmi(), minStop.value, maxStop.value)
  }
  return minStop.value
})
const targetBmi = computed(() => {
  if (isBmiVarSet(fields.value.targetBmiVariable)) {
    return clamp(
      readVarNumber(fields.value.targetBmiVariable, minStop.value),
      minStop.value,
      maxStop.value,
    )
  }
  if (isEditor.value) {
    return clamp(previewTargetBmi(), minStop.value, maxStop.value)
  }
  return minStop.value
})

// ---------- animation ----------

const animTrigger = computed<AnimationTrigger>(() => fields.value.animationTrigger ?? 'mount')
const animDurationMs = computed(() => Math.max(0, Number(fields.value.animationDuration ?? 1200)))
const animDelayMs = computed(() => Math.max(0, Number(fields.value.animationDelay ?? 0)))
const animEasing = computed<AnimationEasing>(() => fields.value.animationEasing ?? 'ease-in-out')

// In editor, don't auto-animate on every field tweak — only the "Preview
// Animation" button or a manual processor call drives motion. Same pattern as
// GoalLineChart.
const isEditor = computed(() => pipe?.currentMode.value === PipeDisplayMode.EDITOR)
const previewing = ref(false)
const animLive = computed(() => !isEditor.value || previewing.value)
const effectiveTrigger = computed<AnimationTrigger>(() =>
  animLive.value ? animTrigger.value : 'none',
)

const running = ref(false)
const progressT = ref(1)
// Seeded from targetBmi in the watch below so the idle state shows the target
// handle at its final position when animation is 'none' / not yet run.
const animatedBmi = ref(22)

let tickRaf: number | null = null

function stopTickLoop() {
  if (tickRaf !== null) cancelAnimationFrame(tickRaf)
  tickRaf = null
}

function ease(t: number, kind: AnimationEasing): number {
  switch (kind) {
    case 'linear':
      return t
    case 'ease-in':
      return t * t
    case 'ease-out':
      return 1 - (1 - t) * (1 - t)
    case 'ease-in-out':
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
    case 'ease':
    default:
      // smoothstep — approximates CSS "ease"
      return t * t * (3 - 2 * t)
  }
}

function startAnimation() {
  stopTickLoop()
  previewing.value = true
  running.value = true
  // Capture `from` so the animation has a stable starting point. `to` is
  // *not* captured — the fitnessfunctions integration may set
  // var.global.targetBmi after BMI Visual mounts (its `immediate: true`
  // watch races with ours), so we re-read targetBmi.value on each tick.
  // The targetBmi watcher below is suppressed while running, otherwise the
  // captured-`to` approach used to leave animatedBmi stuck on a stale
  // (often fallback minStop) value when the integration's update arrived
  // mid-animation.
  const from = currentBmi.value
  const duration = animDurationMs.value
  const delay = animDelayMs.value
  const kind = animEasing.value
  progressT.value = 0
  animatedBmi.value = from

  const startTs = performance.now() + delay
  const tick = (now: number) => {
    const elapsed = now - startTs
    if (elapsed < 0) {
      tickRaf = requestAnimationFrame(tick)
      return
    }
    const t = duration > 0 ? Math.min(1, elapsed / duration) : 1
    progressT.value = t
    const eased = ease(t, kind)
    const to = targetBmi.value
    animatedBmi.value = from + (to - from) * eased
    if (t < 1) {
      tickRaf = requestAnimationFrame(tick)
    } else {
      tickRaf = null
      running.value = false
      previewing.value = false
      // Final snap — re-read targetBmi to pick up any late integration
      // update that arrived during the animation (the watcher below skips
      // updates while running=true).
      animatedBmi.value = targetBmi.value
    }
  }
  tickRaf = requestAnimationFrame(tick)
}

useComponentProcessors({ startAnimation: () => startAnimation() })
useEditorCommands({ startAnimation: () => startAnimation() })

// BMI is always positive — getBmi() never produces 0 or negative numbers
// (it returns null on bad input, which the fitnessfunctions integration then
// drops). So we treat 0 / null / undefined uniformly as "not set" and use
// it to (a) hide the handles + connector while values are still loading
// (avoids a one-frame flicker at the leftmost stop) and (b) defer the
// initial mount animation until real values arrive.
function isBmiVarSet(v: VariableKeyField | undefined): boolean {
  if (!v?.key || !pipe) return false
  const raw = pipe.getData(`var.${v.key}`)
  if (raw === null || raw === undefined) return false
  const n = Number(raw)
  return Number.isFinite(n) && n > 0
}

// In editor mode, treat the BMI as "set" even when the bound variable isn't
// — the currentBmi/targetBmi computeds fall back to preview values, and we
// want the handles / pill / dash to render so authors can preview the chart.
const currentBmiSet = computed(() => isBmiVarSet(fields.value.currentBmiVariable) || isEditor.value)
const targetBmiSet = computed(() => isBmiVarSet(fields.value.targetBmiVariable) || isEditor.value)
const bmiReady = computed(() => currentBmiSet.value && targetBmiSet.value)

// Tracks whether the initial mount animation has already fired so subsequent
// target changes can re-animate (instead of re-triggering on every tick of
// integration setup).
let hasFiredInitialAnimation = false

// Initial mount animation — defers until both BMI variables are populated by
// the pipe. The fitnessfunctions integration's `immediate: true` watch may
// run *after* BMI Visual mounts; without this defer, the animation would
// capture fallback (minStop) values for both `from` and the live tick read,
// so the handle would slide from the leftmost edge.
watch(
  bmiReady,
  (ready) => {
    // SSR (`nuxt generate` prerender): no requestAnimationFrame in Node, and
    // this watcher IS run once during server setup. Bail before the
    // `hasFiredInitialAnimation` flag is set so the client still fires the
    // mount animation — guarding inside startAnimation() instead would leave
    // the flag set and the handle would never travel. Mirrors ScratchTicket.
    if (typeof window === 'undefined') return
    if (!ready) return
    if (hasFiredInitialAnimation) return
    if (effectiveTrigger.value !== 'mount') return
    hasFiredInitialAnimation = true
    startAnimation()
  },
  { immediate: true },
)

// Sync / re-animate on target changes:
// - 'mount' mode + already animated once → re-run the animation so the
//   handle visibly travels from the current BMI to the new target.
// - Other modes (or before initial fire) → snap, no animation.
// Skip both if an animation is currently running; the tick loop re-reads
// targetBmi each frame, so an in-flight animation already picks up the new
// target on its own.
watch(
  targetBmi,
  (v) => {
    if (running.value) return
    if (hasFiredInitialAnimation && effectiveTrigger.value === 'mount') {
      startAnimation()
      return
    }
    animatedBmi.value = v
  },
  { immediate: true },
)

// If trigger is dynamically flipped to 'mount' after the component is
// already alive (e.g., author edits the field), kick off the deferred
// initial animation if values are ready. The bmiReady watch alone wouldn't
// catch this case because it doesn't re-fire when the *mode* changes.
watch(effectiveTrigger, (mode) => {
  if (mode === 'mount' && bmiReady.value && !hasFiredInitialAnimation) {
    hasFiredInitialAnimation = true
    startAnimation()
  }
})

onBeforeUnmount(() => {
  stopTickLoop()
})

// ---------- derived visual values ----------

const currentX = computed(() => bmiToX(currentBmi.value))
const animatedX = computed(() => bmiToX(animatedBmi.value))
const animatedPctCss = computed(() => `${bmiToPct(animatedBmi.value)}%`)

// Resolve the configurable bar gradient into a list of {position, color} for
// the SVG <linearGradient>. Falls back to the design's three-stop default
// when the field is unset or invalid (less than 2 stops).
type ResolvedGradientStop = { position: number; color: string }
const DEFAULT_GRADIENT_STOPS: ResolvedGradientStop[] = [
  { position: 0, color: '#BEC6F4' },
  { position: 42, color: '#A9DFBA' },
  { position: 100, color: '#E37E8E' },
]
const resolvedGradientStops = computed<ResolvedGradientStop[]>(() => {
  const g = fields.value.barGradient
  if (!g || g.type !== 'linear_gradient' || !g.stops || g.stops.length < 2) {
    return DEFAULT_GRADIENT_STOPS
  }
  return [...g.stops]
    .sort((a, b) => a.position - b.position)
    .map((s) => ({
      position: s.position,
      color: getColorStyleData(s.color) ?? 'transparent',
    }))
})

// ---------- scoped variables for TipTap interpolation ----------

useComponentScopedVariables('BMIVisual', () => ({
  currentBmi: currentBmi.value,
  targetBmi: targetBmi.value,
  animatedBmi: animatedBmi.value,
  progress: progressT.value,
}))

// ---------- CSS variable bag ----------

function col(f: ColorField | undefined, fallback: string): string {
  return getColorStyleData(f) || fallback
}
function sz(f: SizeField | undefined, fallback: string): string {
  return getSizeStyleData(f) || fallback
}

const css = computed(() => ({
  padding: getSpacingStyleData(fields.value.padding, '24px') ?? '24px',
  margin: getSpacingStyleData(fields.value.margin, '0') ?? '0',
  cardBg: col(fields.value.cardBgColor, '#FFFFFF'),
  cardBorder: col(fields.value.cardBorderColor, '#03CEA4'),
  cardRadius: sz(fields.value.cardBorderRadius, '24px'),

  titleColor: col(fields.value.titleColor, '#0B3E53'),
  titleFontSize: sz(fields.value.titleFontSize, '16px'),
  titleFontWeight: fields.value.titleFontWeight || '600',
  titleFontFamily: fields.value.titleFontFamily || 'inherit',

  targetTextColor: col(fields.value.targetTextColor, '#0B3E53'),
  targetTextFontSize: sz(fields.value.targetTextFontSize, '32px'),
  targetTextFontWeight: fields.value.targetTextFontWeight || '400',
  targetTextFontFamily: fields.value.targetTextFontFamily || 'inherit',

  stopLabelColor: col(fields.value.stopLabelColor, '#0B3E53'),
  stopLabelFontSize: sz(fields.value.stopLabelFontSize, '14px'),
  stopLabelFontWeight: fields.value.stopLabelFontWeight || '500',
  stopLabelFontFamily: fields.value.stopLabelFontFamily || 'inherit',
  stopLabelDistance: sz(fields.value.stopLabelDistance, '8px'),

  currentHandle: col(fields.value.currentHandleColor, '#85A29E'),
  currentHandleOpacity: String(fields.value.currentHandleOpacity ?? 0.5),
  targetHandle: col(fields.value.targetHandleColor, '#099F96'),

  pillBg: col(fields.value.handleLabelBgColor, '#FFFFFF'),
  pillBorder: col(fields.value.handleLabelBorderColor, '#DAF1ED'),
  pillText: col(fields.value.handleLabelTextColor, '#587470'),
  pillFontSize: sz(fields.value.handleLabelFontSize, '14px'),
  pillFontWeight: fields.value.handleLabelFontWeight || '400',
  pillFontFamily: fields.value.handleLabelFontFamily || 'inherit',
  pillRadius: sz(fields.value.handleLabelBorderRadius, '8px'),
  pillBorderWidth: sz(fields.value.handleLabelBorderWidth, '1px'),
  dashColor: col(fields.value.dashedLineColor, '#DAF1ED'),

  bottomLabelColor: col(fields.value.bottomLabelColor, '#0B3E53'),
  bottomLabelFontSize: sz(fields.value.bottomLabelFontSize, '14px'),
  bottomLabelFontWeight: fields.value.bottomLabelFontWeight || '400',
  bottomLabelFontFamily: fields.value.bottomLabelFontFamily || 'inherit',
}))
</script>

<style scoped lang="scss">
.bmi-visual {
  display: block;
  box-sizing: border-box;
  background: v-bind('css.cardBg');
  border: 1px solid v-bind('css.cardBorder');
  border-radius: v-bind('css.cardRadius');
  padding: v-bind('css.padding');
  margin: v-bind('css.margin');
}

.bmi-title {
  color: v-bind('css.titleColor');
  font-size: v-bind('css.titleFontSize');
  font-weight: v-bind('css.titleFontWeight');
  font-family: v-bind('css.titleFontFamily');
  line-height: 1.3;
  margin-bottom: 6px;

  :deep(.ProseMirror) {
    min-height: 0;
    padding: 0;
  }
  :deep(p) {
    margin: 0;
  }
}

.bmi-target-text {
  color: v-bind('css.targetTextColor');
  font-size: v-bind('css.targetTextFontSize');
  font-weight: v-bind('css.targetTextFontWeight');
  font-family: v-bind('css.targetTextFontFamily');
  line-height: 1.3;
  margin-bottom: 16px;

  :deep(.ProseMirror) {
    min-height: 0;
    padding: 0;
  }
  :deep(p) {
    margin: 0;
  }
}

.bmi-chart-wrap {
  position: relative;
  padding-top: 36px;
  margin-bottom: 14px;
}

.bmi-pill {
  position: absolute;
  top: 28px;
  transform: translate(-50%, -100%);
  display: inline-flex;
  align-items: center;
  padding: 4px 14px;
  border: v-bind('css.pillBorderWidth') solid v-bind('css.pillBorder');
  border-radius: v-bind('css.pillRadius');
  background: v-bind('css.pillBg');
  color: v-bind('css.pillText');
  font-size: v-bind('css.pillFontSize');
  font-weight: v-bind('css.pillFontWeight');
  font-family: v-bind('css.pillFontFamily');
  line-height: 1.2;
  white-space: nowrap;
  z-index: 2;

  :deep(.ProseMirror) {
    min-height: 0;
    padding: 0;
  }
  :deep(p) {
    margin: 0;
    white-space: nowrap;
  }
}

.bmi-dash {
  position: absolute;
  // Spans from just below the pill down to the bar / handle top. The SVG top
  // sits at chart-wrap padding-top (36px) + stops-row height (1em, because
  // line-height is 1 and font-size === stopLabelFontSize) + labelDistance.
  // Dash starts 28px down (just clear of the pill bottom), so:
  //   dash height = (36 + 1em + labelDistance) − 28 = 8px + 1em + labelDistance.
  top: 28px;
  width: 1px;
  height: calc(8px + v-bind('css.stopLabelFontSize') + v-bind('css.stopLabelDistance'));
  transform: translateX(-50%);
  background-image: linear-gradient(to bottom, v-bind('css.dashColor') 50%, transparent 50%);
  background-size: 1px 6px;
  background-repeat: repeat-y;
  z-index: 1;
}

.bmi-stops-row {
  position: relative;
  height: 1em;
  margin-bottom: v-bind('css.stopLabelDistance');
  color: v-bind('css.stopLabelColor');
  font-size: v-bind('css.stopLabelFontSize');
  font-weight: v-bind('css.stopLabelFontWeight');
  font-family: v-bind('css.stopLabelFontFamily');
  line-height: 1;

  :deep(.ProseMirror) {
    min-height: 0;
    padding: 0;
  }
  :deep(p) {
    margin: 0;
    white-space: nowrap;
  }
}

.bmi-stop {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  white-space: nowrap;
}

.bmi-chart-svg {
  display: block;
  width: 100%;
  aspect-ratio: 342 / 40;
  overflow: visible;

  .handle-current {
    fill: v-bind('css.currentHandle');
    opacity: v-bind('css.currentHandleOpacity');
    stroke: white;
    stroke-width: 3;
  }
  .handle-target {
    fill: v-bind('css.targetHandle');
    stroke: white;
    stroke-width: 3;
  }

  .bmi-dash {
    stroke: v-bind('css.dashColor');
    stroke-width: 1;
    fill: none;
  }
}

.bmi-bottom-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  color: v-bind('css.bottomLabelColor');
  font-size: v-bind('css.bottomLabelFontSize');
  font-weight: v-bind('css.bottomLabelFontWeight');
  font-family: v-bind('css.bottomLabelFontFamily');

  .bmi-bottom-left {
    text-align: start;
    min-width: 0;
  }
  .bmi-bottom-right {
    text-align: end;
    min-width: 0;
  }

  :deep(.ProseMirror) {
    min-height: 0;
    padding: 0;
  }
  :deep(p) {
    margin: 0;
  }
}
</style>
